/** API della mappa delle automazioni (SPEC-014.2 §B.2). Sola lettura. */

import { useQuery } from '@tanstack/react-query';
import type { AssetDomain } from '@/lib/asset-domain';
import { api } from '@/lib/api-client';

/** `conditional` = ci si arriva solo con la spunta di conferma (Parte A). */
export type LeadsToTrading = 'always' | 'conditional' | 'never';

export type Schedule = {
  key: string;
  label: string;
  cadence: string;
  enabled: boolean;
  disabledReason: string | null;
  lastRunAt: string | null;
  lastRunOutcome: string | null;
  /** `false` = questo innesco non lascia traccia: si mostra «non disponibile». */
  lastRunTracked: boolean;
  leadsToTrading: LeadsToTrading;
  effect: string;
  /** F-144: quante fasi di fine giornata girano in modalita' agentica e quante standard. */
  agentic?: { markets: number; standard: number } | null;
  /** Gli ambiti che il flusso serve (SPEC-026 §2.6). */
  domains: AssetDomain[];
  /** F-185: dove si accende e si configura, per gli inneschi con un interruttore per utente. */
  href?: string | null;
  /** La prossima partenza, quando l'innesco ha un orario per utente ed è acceso. */
  nextRunAt?: string | null;
};

export type ManualTrigger = {
  key: string;
  label: string;
  where: string;
  leadsToTrading: LeadsToTrading;
  effect: string;
  domains: AssetDomain[];
};

export type AutomationSwitch = {
  key: string;
  label: string;
  /** Solo acceso/spento: mai il valore di un segreto. */
  on: boolean;
  scope: 'server' | 'user' | 'connection';
  detail: string;
  href: string | null;
};

export type AutomationsOverview = {
  schedules: Schedule[];
  manualTriggers: ManualTrigger[];
  switches: AutomationSwitch[];
};

export function useAutomationsOverview() {
  return useQuery({
    queryKey: ['automations', 'overview'] as const,
    queryFn: ({ signal }) => api.get<AutomationsOverview>('/automations/overview', signal),
  });
}

/** F-168: gli stessi codici del CHECK `automation_journal_flow_known`. */
export type JournalFlow =
  | 'market_open_trigger'
  | 'portfolio_briefing'
  | 'auto_trading_evaluate'
  | 'auto_trading_resume'
  | 'profit_review'
  | 'market_close_trigger'
  | 'news_review';

export type JournalOutcome = 'done' | 'skipped' | 'deferred' | 'failed';

export type JournalEntry = {
  id: string;
  flow: JournalFlow;
  outcome: JournalOutcome;
  reasonCode: string;
  detail: string;
  domain: AssetDomain | null;
  subjectType: string | null;
  subjectId: string | null;
  /** Quante volte la stessa decisione si e' ripetuta (i poller girano ogni 5 minuti). */
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
};

/**
 * Diario degli inneschi (F-168): cosa ha deciso ogni flusso negli ultimi
 * giorni, comprese le decisioni di non fare niente.
 */
export function useAutomationJournal(days = 3) {
  return useQuery({
    queryKey: ['automations', 'journal', days] as const,
    queryFn: ({ signal }) => api.get<{ entries: JournalEntry[] }>(`/automations/journal?days=${days}`, signal),
  });
}
